import type { ReactNode, RefObject } from 'react';

import React, { Children, Fragment } from 'react';

const getWrapperElement = (element: HTMLElement): HTMLElement => {
  while (element && element.childElementCount === 1) {
    element = element.firstChild as HTMLElement;
  }
  return element;
};

interface OffsetParamsType {
  offset: number;
}

const getOffsetParams = (
  carouselRef: RefObject<HTMLDivElement>,
  activeIndex: number,
): OffsetParamsType => {
  if (!carouselRef.current) return { offset: 0 };

  const wrapperElement = getWrapperElement(carouselRef.current)?.children;

  return {
    offset:
      (wrapperElement[activeIndex] as HTMLElement)?.offsetLeft ??
      (wrapperElement[wrapperElement.length - 1] as HTMLElement)?.offsetLeft,
  };
};

export const isEndOfScrollReached = (el: HTMLElement): boolean => {
  const { scrollWidth, scrollLeft, clientWidth } = el;

  return Math.floor(scrollWidth - scrollLeft) <= clientWidth;
};

const getIndexByElement = (el: HTMLElement): number => {
  const { childNodes, scrollLeft, clientWidth } = el;
  let nextOffset = 0;

  const isFullWidthItem = (childNodes[0] as HTMLElement)?.clientWidth === clientWidth;
  const gap = parseInt(getComputedStyle(el).gap);
  const endOfScroll = isEndOfScrollReached(el);

  if (scrollLeft === 0) return 0;

  for (let i = 0; i < childNodes.length; ++i) {
    const currentGap = i === 0 ? 0 : gap;
    if (nextOffset > scrollLeft) {
      const isPreviousItemHidden = scrollLeft > (childNodes[i - 1] as HTMLElement).offsetLeft;
      return endOfScroll && isPreviousItemHidden ? i : i - 1;
    } else if (isFullWidthItem && endOfScroll && i === 0) {
      return childNodes.length - 1;
    }
    nextOffset += childNodes[i]
      ? +Math.ceil((childNodes[i] as HTMLElement).offsetWidth) + currentGap
      : 0;
  }

  return 0;
};

const getActiveIndex = (carouselRef: RefObject<HTMLDivElement>): number => {
  if (!carouselRef.current) return 0;

  const wrapperElement = getWrapperElement(carouselRef.current);
  const { scrollLeft } = wrapperElement || {};

  for (let i = 0; wrapperElement && i < wrapperElement.children.length; i++) {
    const child = wrapperElement.children[i] as HTMLElement;
    if (child.offsetLeft + child.offsetWidth / 2 > scrollLeft) {
      return i;
    }
  }

  return 0;
};

const countChildren = (children: ReactNode): number => {
  const normalizedChildren = Children.toArray(children);
  let count = 0;

  normalizedChildren.forEach((child) => {
    if (!React.isValidElement(child)) return;

    if (child.type === Fragment) {
      count += countChildren(child.props.children);
    } else {
      count += 1;
    }
  });

  return count;
};

export { countChildren, getActiveIndex, getIndexByElement, getOffsetParams, getWrapperElement };
